import { useMutation, useQueryClient } from "@tanstack/react-query";

import { translateDomainError } from "#app/i18n/translateApiError";
import { errorHandler } from "#app/monitoring/errorHandler";
import { showErrorToast } from "#app/notifications";
import { getCompanyQueryData, setCompanyQueryData } from "#modules/company/infra/company.query";
import {
  createDefaultProcessStep,
  CreateDefaultProcessStep,
} from "#modules/defaultProcessSteps/infra/createDefaultProcessStep.api";
import { isDomainError } from "shared";

export const useCreateDefaultProcessStep = (options?: {
  onSuccess?: (defaultProcessStep: CreateDefaultProcessStep) => void;
}) => {
  const queryClient = useQueryClient();

  const { mutate, ...rest } = useMutation({
    mutationFn: createDefaultProcessStep,
    onMutate: () => {},
    onSuccess: (defaultProcessStep) => {
      setCompanyQueryData(queryClient, (currentCompany) => {
        const companyToUpdate = currentCompany ?? getCompanyQueryData(queryClient);
        if (!companyToUpdate) {
          return companyToUpdate;
        }

        return {
          ...companyToUpdate,
          companySettings: {
            ...companyToUpdate.companySettings,
            defaultProcessSteps: [
              ...companyToUpdate.companySettings.defaultProcessSteps,
              defaultProcessStep,
            ],
          },
        };
      });
      options?.onSuccess?.(defaultProcessStep);
    },
    onError: (err: Error) => {
      if (isDomainError(err)) {
        showErrorToast({ title: translateDomainError(err) });
      } else {
        showErrorToast({ title: err.message });
      }
      errorHandler(err);
    },
  });

  return { createDefaultProcessStep: mutate, ...rest };
};
